/**
 * Zod validation schemas for IntegrationProfile JSONB fields
 *
 * These schemas provide runtime validation for the `config_json` JSONB column
 * in the `integration_profiles` table, matching the interfaces defined in
 * integration-profile.types.ts.
 *
 * **Architecture Note**: Imports const arrays from integration-profile.types.ts
 * as single source of truth for enum values. This eliminates duplication and ensures
 * type/schema consistency.
 */
import { z } from 'zod';
import { ACQUISITION_DEDUPE_SCOPE_VALUES, ACQUISITION_FAILURE_MODE_VALUES, ACQUISITION_HANDOFF_MODE_VALUES, ACQUISITION_POST_ACTION_MODE_VALUES, ACQUISITION_SCHEDULER_MODE_VALUES, API_AUTH_TYPE_VALUES, API_HTTP_METHOD_VALUES, SFTP_DIRECTORY_NAME_VALUES, } from './integration-profile.types.js';
// ==================== SFTP DELIVERY SCHEMAS ====================
export const SftpDirectoryNameSchema = z.enum(SFTP_DIRECTORY_NAME_VALUES);
export const SftpDeliveryDirectorySchema = z
    .object({
    name: z.string().min(1),
    path: z
        .string()
        .min(2)
        .refine((value) => value.startsWith('/'), {
        message: 'Directory path must start with a leading slash (POSIX path)',
    })
        .refine((value) => !value.endsWith('/'), {
        message: 'Directory path must not end with a trailing slash',
    })
        .refine((value) => !value.includes('\\'), {
        message: 'Directory path must be POSIX-style (no backslashes)',
    }),
})
    .passthrough();
export const SftpDeliveryConfigSchema = z
    .object({
    secretArn: z.string().min(1),
    directories: z.array(SftpDeliveryDirectorySchema).min(1),
})
    .passthrough();
// ==================== API DELIVERY SCHEMAS ====================
export const ApiAuthTypeSchema = z.enum(API_AUTH_TYPE_VALUES);
export const ApiHttpMethodSchema = z.enum(API_HTTP_METHOD_VALUES);
export const ApiDeliveryAuthConfigSchema = z
    .object({
    type: ApiAuthTypeSchema,
    secretArn: z.string().min(1).optional(),
    apiKeyHeader: z.string().min(1).optional(),
})
    .passthrough();
export const ApiDeliveryEndpointConfigSchema = z
    .object({
    path: z.string().min(1),
    method: ApiHttpMethodSchema.optional(),
    headers: z.record(z.string(), z.string()).optional(),
    timeoutMs: z.number().int().positive().optional(),
})
    .passthrough();
export const ApiDeliveryConfigSchema = z
    .object({
    baseUrl: z.string().url('baseUrl must be a valid URL'),
    endpointPath: z.string().min(1).optional(),
    method: ApiHttpMethodSchema.optional(),
    auth: ApiDeliveryAuthConfigSchema.optional(),
    headers: z.record(z.string(), z.string()).optional(),
    timeoutMs: z.number().int().positive().optional(),
    endpoints: z.record(z.string().min(1), ApiDeliveryEndpointConfigSchema).optional(),
})
    .passthrough();
export const IntegrationProfileDeliveryConfigSchema = z
    .object({
    sftp: SftpDeliveryConfigSchema.optional(),
    api: ApiDeliveryConfigSchema.optional(),
})
    .passthrough();
// ==================== ACQUISITION SCHEMAS ====================
export const AcquisitionSchedulerModeSchema = z.enum(ACQUISITION_SCHEDULER_MODE_VALUES);
export const AcquisitionHandoffModeSchema = z.enum(ACQUISITION_HANDOFF_MODE_VALUES);
export const AcquisitionDedupeScopeSchema = z.enum(ACQUISITION_DEDUPE_SCOPE_VALUES);
export const AcquisitionPostActionModeSchema = z.enum(ACQUISITION_POST_ACTION_MODE_VALUES);
export const AcquisitionFailureModeSchema = z.enum(ACQUISITION_FAILURE_MODE_VALUES);
export const AcquisitionDedupeConfigSchema = z
    .object({
    strategy: z.literal('checksum'),
    scope: AcquisitionDedupeScopeSchema.default('party_file_format'),
})
    .passthrough();
export const AcquisitionPostActionConfigSchema = z
    .object({
    mode: AcquisitionPostActionModeSchema,
})
    .passthrough();
export const AcquisitionHandoffConfigSchema = z
    .object({
    mode: AcquisitionHandoffModeSchema,
    acquiredEventQueueUrl: z.string().min(1).optional(),
})
    .passthrough();
export const AcquisitionFailurePolicyConfigSchema = z
    .object({
    mode: AcquisitionFailureModeSchema,
    maxRetries: z.number().int().nonnegative().optional(),
})
    .passthrough();
export const AcquisitionFileFormatRuleSchema = z
    .object({
    fileFormatId: z.number().int().positive(),
    enabled: z.boolean().optional(),
    remoteDirectoryPath: z
        .string()
        .min(2)
        .refine((value) => value.startsWith('/'), {
        message: 'remoteDirectoryPath must start with a leading slash (POSIX path)',
    })
        .refine((value) => !value.endsWith('/'), {
        message: 'remoteDirectoryPath must not end with a trailing slash',
    })
        .refine((value) => !value.includes('\\'), {
        message: 'remoteDirectoryPath must be POSIX-style (no backslashes)',
    })
        .refine((value) => !value.split('/').includes('..'), {
        message: 'remoteDirectoryPath must not contain ".." path segments',
    }),
    fileNameRegex: z
        .string()
        .min(1)
        .refine((val) => {
        try {
            new RegExp(val);
            return true;
        }
        catch {
            return false;
        }
    }, { message: 'fileNameRegex must be a valid regular expression' })
        .optional(),
    dedupe: AcquisitionDedupeConfigSchema.optional(),
    postAction: AcquisitionPostActionConfigSchema.optional(),
    handoff: AcquisitionHandoffConfigSchema.optional(),
    failurePolicy: AcquisitionFailurePolicyConfigSchema.optional(),
    scheduleCron: z.string().min(1).optional(),
    timezone: z.string().min(1).optional(),
})
    .passthrough();
export const SftpAcquisitionPollingConfigSchema = z
    .object({
    enabled: z.boolean().optional(),
    secretArn: z.string().min(1),
    schedulerMode: AcquisitionSchedulerModeSchema.default('hybrid'),
    scheduleCron: z.string().min(1).optional(),
    timezone: z.string().min(1).optional(),
    rules: z.array(AcquisitionFileFormatRuleSchema).min(1),
})
    .passthrough();
export const IntegrationProfileAcquisitionConfigSchema = z
    .object({
    sftp: SftpAcquisitionPollingConfigSchema.optional(),
})
    .passthrough();
// ==================== CONFIG ROOT SCHEMA ====================
/**
 * IntegrationProfile Config Schema
 * Runtime validation for the `config_json` JSONB field.
 */
export const IntegrationProfileConfigSchema = z
    .object({
    delivery: IntegrationProfileDeliveryConfigSchema.optional(),
    acquisition: IntegrationProfileAcquisitionConfigSchema.optional(),
    schema_version: z.string().optional(),
})
    .passthrough();
//# sourceMappingURL=integration-profile.schemas.js.map